import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createBlock as _createBlock, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, normalizeClass as _normalizeClass, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "agila-date-picker" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = ["value"]
const _hoisted_4 = ["value"]
const _hoisted_5 = ["value"]

import {
  ref,
  watch,
} from "vue";
import AgilaFormError from "./agilaFormError.vue";
import useSharedFormLogic from "../helper/functions/useSharedFormLogic";
import { PRIMEVUE_CONFIG } from "../store/primevue";

interface Props {
  minDate?: Date | string;
  maxDate?: Date | string;
  scrollHeight?: string;
  label?: string;
  selectedDate?: Date;
  showError?: boolean;
  rules?: string[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'agilaDatePicker',
  props: {
    minDate: { default: undefined },
    maxDate: { default: undefined },
    scrollHeight: { default: "394px" },
    label: { default: "" },
    selectedDate: { default: undefined },
    showError: { type: Boolean, default: false },
    rules: { default: () => [] }
  },
  emits: ["update:selectedDate", "selectDate", "update:modelValue", "error"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const { readyForCheck, inputValue, error, updateValue, validate } =
  useSharedFormLogic(props, emit);

__expose({
  validate: () => {
    readyForCheck.value = true;
    return validate(selectedDate.value);
  },
});

const dateToday: Date = new Date();
const minDate = ref<Date | undefined>(
  props.minDate
    ? typeof props.minDate === "string"
      ? new Date(props.minDate)
      : props.minDate
    : undefined
);
const maxDate = ref<Date | undefined>(
  props.maxDate
    ? typeof props.maxDate === "string"
      ? new Date(props.maxDate)
      : props.maxDate
    : undefined
);
const minYear = ref<number>(
  minDate.value ? minDate.value.getFullYear() : dateToday.getFullYear() - 100
);
const maxYear = ref<number>(
  maxDate.value ? maxDate.value.getFullYear() : dateToday.getFullYear()
);
const dayOptions = ref<string[]>(createDaysArray(31));
const monthOptions = ref<string[]>(PRIMEVUE_CONFIG.locale.monthNames);
const yearOptions = ref<string[]>(
  Array.from({ length: maxYear.value - minYear.value + 1 }, (_, index) =>
    (maxYear.value - index).toString()
  )
);
const selectedDate = ref<Date | undefined>(props.selectedDate);
const selectedDay = ref<string>(
  selectedDate.value?.getDate().toString() || "Tag"
);
const selectedMonth = ref<string>(
  selectedDate.value
    ? monthOptions.value[Number(selectedDate.value?.getMonth())]
    : "Monat"
);
const selectedYear = ref<string>(
  selectedDate.value?.getFullYear().toString() || "Jahr"
);

function createDaysArray(days: number, start = 1): Array<string> {
  return Array.from({ length: days }, (_, index) => (index + start).toString());
}

function calculateDate(): void {
  if (selectedMonth.value) {
    const lastDayOfMonth = new Date(
      Number(selectedYear.value) || dateToday.getFullYear(),
      monthOptions.value.indexOf(selectedMonth.value) + 1,
      0
    ).getDate();
    const diff =
      Number(dayOptions.value[dayOptions.value.length - 1]) - lastDayOfMonth;

    if (diff > 0) {
      dayOptions.value = dayOptions.value.slice(0, diff * -1);
    } else {
      dayOptions.value = createDaysArray(31);
    }

    if (selectedDay.value && Number(selectedDay.value) > lastDayOfMonth) {
      selectedDay.value = lastDayOfMonth.toString();
    }
  }

  if (
    selectedDay.value &&
    selectedMonth.value &&
    selectedYear.value &&
    selectedDay.value !== "Tag" &&
    selectedMonth.value !== "Monat" &&
    selectedYear.value !== "Jahr"
  ) {
    selectedDate.value = new Date(
      Number(selectedYear.value),
      monthOptions.value.indexOf(selectedMonth.value),
      Number(selectedDay.value)
    );
    setDate();
  }
}

function setDate(): void {
  if (selectedDate.value) {
    validate(selectedDate.value);
    emit("update:selectedDate", selectedDate.value);
    emit("selectDate", selectedDate.value);
  }
}

function setYearOptions(): void {
  yearOptions.value = Array.from(
    { length: maxYear.value - minYear.value + 1 },
    (_, index) => (maxYear.value - index).toString()
  );
}

function resetDate(): void {
  selectedDay.value = undefined;
  selectedMonth.value = undefined;
  selectedYear.value = undefined;
  emit("update:selectedDate", undefined);
  emit("selectDate", undefined);
}

function setDayMonthYear(): void {
  selectedDay.value = selectedDate.value?.getDate().toString() || "Tag";
  selectedMonth.value = selectedDate.value
    ? monthOptions.value[Number(selectedDate.value?.getMonth())]
    : "Monat";
  selectedYear.value = selectedDate.value?.getFullYear().toString() || "Jahr";
}

watch(
  () => props.minDate,
  () => {
    minDate.value = props.minDate
      ? typeof props.minDate === "string"
        ? new Date(props.minDate)
        : props.minDate
      : undefined;
    minYear.value = minDate.value
      ? minDate.value.getFullYear()
      : dateToday.getFullYear() - 100;
    setYearOptions();
    if (
      minDate.value &&
      selectedDate.value &&
      selectedDate.value < minDate.value
    ) {
      resetDate();
    }
  }
);
watch(
  () => props.maxDate,
  () => {
    maxDate.value = props.maxDate
      ? typeof props.maxDate === "string"
        ? new Date(props.maxDate)
        : props.maxDate
      : undefined;
    maxYear.value = maxDate.value
      ? maxDate.value.getFullYear()
      : dateToday.getFullYear();
    setYearOptions();
    if (
      maxDate.value &&
      selectedDate.value &&
      selectedDate.value > maxDate.value
    ) {
      resetDate();
    }
  }
);

watch(
  () => props.selectedDate,
  () => {
    if (props.selectedDate !== selectedDate.value) {
      selectedDate.value = props.selectedDate;
    }
    setDayMonthYear();
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (props.label)
      ? (_openBlock(), _createElementBlock("label", _hoisted_2, _toDisplayString(props.label), 1))
      : _createCommentVNode("", true),
    (props.showError)
      ? (_openBlock(), _createBlock(AgilaFormError, {
          key: 1,
          error: _unref(error)
        }, null, 8, ["error"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(["date-picker-border", { 'input-error': !!_unref(error) }])
    }, [
      _withDirectives(_createElementVNode("select", {
        name: "dropdownDay",
        id: "dropdownDay",
        "aria-label": 'Geburtstag:Tag',
        tabindex: 0,
        "data-cy": "birthday",
        class: _normalizeClass(["date-picker-day", { 'placeholder-color': selectedDay.value === 'Tag' }]),
        onChange: _cache[0] || (_cache[0] = ($event: any) => (calculateDate())),
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedDay).value = $event))
      }, [
        _cache[6] || (_cache[6] = _createElementVNode("option", {
          value: "Tag",
          disabled: "",
          selected: ""
        }, "Tag", -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(dayOptions.value, (option) => {
          return (_openBlock(), _createElementBlock("option", {
            value: option,
            key: option
          }, _toDisplayString(option), 9, _hoisted_3))
        }), 128))
      ], 34), [
        [_vModelSelect, selectedDay.value]
      ]),
      _withDirectives(_createElementVNode("select", {
        name: "dropdownMonth",
        id: "dropdownMonth",
        "aria-label": 'Geburtstag:Monat',
        tabindex: 0,
        "data-cy": "birthmonth",
        class: _normalizeClass(["date-picker-month", { 'placeholder-color': selectedMonth.value === 'Monat' }]),
        onChange: _cache[2] || (_cache[2] = ($event: any) => (calculateDate())),
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedMonth).value = $event))
      }, [
        _cache[7] || (_cache[7] = _createElementVNode("option", {
          value: "Monat",
          disabled: "",
          selected: ""
        }, "Monat", -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthOptions.value, (option) => {
          return (_openBlock(), _createElementBlock("option", {
            value: option,
            key: option
          }, _toDisplayString(option), 9, _hoisted_4))
        }), 128))
      ], 34), [
        [_vModelSelect, selectedMonth.value]
      ]),
      _withDirectives(_createElementVNode("select", {
        name: "dropdownYear",
        id: "dropdownYear",
        "aria-label": 'Geburtstag:Jahr',
        tabindex: 0,
        "data-cy": "birthyear",
        class: _normalizeClass(["date-picker-year", { 'placeholder-color': selectedYear.value === 'Jahr' }]),
        onChange: _cache[4] || (_cache[4] = ($event: any) => (calculateDate())),
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedYear).value = $event))
      }, [
        _cache[8] || (_cache[8] = _createElementVNode("option", {
          value: "Jahr",
          disabled: "",
          selected: ""
        }, "Jahr", -1)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(yearOptions.value, (option) => {
          return (_openBlock(), _createElementBlock("option", {
            value: option,
            key: option
          }, _toDisplayString(option), 9, _hoisted_5))
        }), 128))
      ], 34), [
        [_vModelSelect, selectedYear.value]
      ])
    ], 2)
  ]))
}
}

})